import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserRole } from '../../contexts/UserRoleContext';
import './Navigation.css';

interface NavigationProps {
  onSectionChange: (section: string) => void;
  /** Slug for current location (e.g. "oakland"); null when no location selected */
  locationSlug: string | null;
  /** Current pathname for building links and aria-current */
  currentPath: string;
}

const KNOWN_SECTIONS = new Set([
  'overview',
  'transportation',
  'buildings',
  'other',
  'people',
  'ghg',
  'policy-tool',
  'coolclimate-calculator'
]);

function buildSectionPath(slug: string | null, section: string): string {
  if (!slug) return section === 'overview' ? '/overview' : `/${section}`;
  return section === 'overview' ? `/${slug}` : `/${slug}/${section}`;
}

export const Navigation: React.FC<NavigationProps> = ({ onSectionChange, locationSlug, currentPath }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { hasAnyRole } = useUserRole();

  const baseNavItems = [
    { section: 'overview', label: 'Overview →', color: 'blue' },
    { section: 'transportation', label: 'Transportation →', color: 'light-blue' },
    { section: 'buildings', label: 'Buildings →', color: 'gold' },
    { section: 'other', label: 'Waste +', color: 'orange' },
    { section: 'people', label: 'People →', color: 'pink' },
    { section: 'ghg', label: 'GHG inventory →', color: 'teal' }
  ];

  // Policy Tool is now available to all users
  const publicNavItems = [
    ...baseNavItems,
    { section: 'policy-tool', label: 'Policy Tool →', color: 'purple' }
  ];

  // Calculator tab: only for developers and super admins (not local government admins)
  const showCalculator = hasAnyRole(['super_admin', 'developer']);
  const navItems = showCalculator
    ? [...publicNavItems, { section: 'coolclimate-calculator', label: 'Calculator →', color: 'gray' }]
    : publicNavItems;

  const handleNavClick = (section: string, e: React.MouseEvent | React.KeyboardEvent) => {
    const knownSections = KNOWN_SECTIONS;
    const parts = currentPath.split('/').filter(Boolean);
    const isOverviewPath =
      parts.length === 0 ||
      (parts.length === 1 && !knownSections.has(parts[0])) ||
      (parts.length === 2 && parts[1] === 'overview');
    const isOnOverviewPage = isOverviewPath;
    const overviewFullyLoaded = typeof window !== 'undefined' && (window as any).overviewFullyLoaded === true;

    if (isOnOverviewPage && !overviewFullyLoaded && section !== 'transportation' && section !== 'overview') {
      e.preventDefault();
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentSection = () => {
    const knownSections = KNOWN_SECTIONS;
    const parts = currentPath.split('/').filter(Boolean);
    if (parts.length === 0) return 'overview';
    if (parts.length === 1) return knownSections.has(parts[0]) ? parts[0] : 'overview';
    return parts[1] || 'overview';
  };

  const currentSection = getCurrentSection();

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const target = event.target as Element;
        if (!target.closest('.nav-bar')) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <nav className="nav-bar" aria-label="Main navigation">
      {/* Mobile Title */}
      <div className="mobile-nav-title" aria-hidden="true">
        ClimatePlans
      </div>

      {/* Mobile Hamburger Button */}
      <button
        type="button"
        className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle navigation menu"
        aria-expanded={isMobileMenuOpen}
      >
        <span className="hamburger-line" aria-hidden="true"></span>
        <span className="hamburger-line" aria-hidden="true"></span>
        <span className="hamburger-line" aria-hidden="true"></span>
      </button>

      {/* Navigation Items - use links for keyboard and screen reader access */}
      <div className={`nav-items ${isMobileMenuOpen ? 'mobile-open' : ''}`} role="list">
        {navItems.map((item) => {
          const path = buildSectionPath(locationSlug, item.section);
          const isActive = currentSection === item.section;
          return (
            <Link
              key={item.section}
              to={path}
              className={`nav-item ${item.color} ${isActive ? 'active' : ''}`}
              onClick={(e) => handleNavClick(item.section, e)}
              aria-current={isActive ? 'page' : undefined}
              role="listitem"
            >
              {item.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
